import type {
  CopilotMarkdownExtension,
  ReactComponentsExtension,
  RehypeHtmlTransformer,
} from '@github-ui/copilot-markdown/extension'
import {dataAttrToPropName} from '@github-ui/copilot-markdown/utils'
import type {LabelColorOptions} from '@primer/react'
import type {Element, ElementContent, Parents} from 'hast'
import {visit} from 'unist-util-visit'

import {STATS_DATA_ATTR, type TableBadgeStats} from './stats'
import {TableBadge} from './TableBadge'
import {TableScrollWrapper} from './TableScrollWrapper'
import {VALUE_TO_VARIANT} from './value-to-variant'

const BADGE_DATA_ATTR = 'data-copilot-chat-badge'
const BADGE_PROPERTY_NAME = dataAttrToPropName(BADGE_DATA_ATTR)
const BADGE_VARIANT_DATA_ATTR = 'data-copilot-chat-badge-variant'
const BADGE_VARIANT_PROPERTY_NAME = dataAttrToPropName(BADGE_VARIANT_DATA_ATTR)
const WRAPPER_DATA_ATTR = 'data-copilot-chat-table-wrap'
const WRAPPER_PROPERTY_NAME = dataAttrToPropName(WRAPPER_DATA_ATTR)
const STATS_PROPERTY_NAME = dataAttrToPropName(STATS_DATA_ATTR)

function newStats(): TableBadgeStats {
  return {
    totalColumns: 0,
    badgedColumns: 0,
    rejectionReasons: {hasLink: 0, mixedValues: 0, noRealValues: 0, noEligibleValues: 0},
  }
}

/** Recursively flatten the text content of a hast element. */
function flattenText(nodes: readonly ElementContent[]): string {
  let out = ''
  for (const node of nodes) {
    if (node.type === 'text') {
      out += node.value
    } else if (node.type === 'element') {
      out += flattenText(node.children)
    }
  }
  return out
}

function buildBadgeElement(originalValue: string, variant: LabelColorOptions): Element {
  return {
    type: 'element',
    tagName: 'span',
    properties: {
      [BADGE_PROPERTY_NAME]: originalValue,
      [BADGE_VARIANT_PROPERTY_NAME]: variant,
    },
    children: [{type: 'text', value: originalValue}],
  }
}

// Cells whose trimmed text matches one of these are treated as "no data" and
// neither badged nor counted against a column's eligibility.
const EMPTY_CELL_MARKERS = new Set(['', '-', '–', '—'])

function isEmptyCell(value: string): boolean {
  return EMPTY_CELL_MARKERS.has(value)
}

const TOKEN_SEPARATOR = /[\s\-–—/+,&()]+/

// Words allowed between/around allowlisted tokens without rejecting the cell.
// Kept narrow on purpose: vague words like `is` or `with` could let real prose
// slip through.
const ALLOWED_CONNECTORS = new Set<string>([
  'very',
  'extremely',
  'rather',
  'quite',
  'slightly',
  'somewhat',
  'fairly',
  'moderately',
  'mildly',
  'to',
  'or',
  'and',
  'a',
  'an',
  'the',
  'of',
])

function lookupVariant(key: string): LabelColorOptions | undefined {
  return Object.hasOwn(VALUE_TO_VARIANT, key) ? VALUE_TO_VARIANT[key] : undefined
}

// Direct lookup first (catches multi-word entries like `in progress`), then
// tokenize and require every token to be allowlisted or a connector. Variant
// is the first allowlisted token in reading order, so `Medium-High` badges as
// Medium and `Very High` badges as High.
function findVariantForCell(value: string): LabelColorOptions | null {
  const normalized = value.toLowerCase().trim()
  const fastPath = lookupVariant(normalized)
  if (fastPath !== undefined) return fastPath

  const tokens = normalized.split(TOKEN_SEPARATOR).filter(Boolean)
  if (tokens.length === 0) return null

  let firstVariant: LabelColorOptions | null = null
  for (const token of tokens) {
    const variant = lookupVariant(token)
    if (variant !== undefined) {
      if (firstVariant === null) firstVariant = variant
    } else if (!ALLOWED_CONNECTORS.has(token)) {
      return null
    }
  }
  return firstVariant
}

function tableBodyRows(table: Element): Element[] {
  const rows: Element[] = []
  for (const child of table.children) {
    if (child.type !== 'element' || child.tagName !== 'tbody') continue
    for (const row of child.children) {
      if (row.type === 'element' && row.tagName === 'tr') rows.push(row)
    }
  }
  return rows
}

function rowBodyCells(row: Element): Element[] {
  return row.children.filter((child): child is Element => child.type === 'element' && child.tagName === 'td')
}

function containsTag(nodes: readonly ElementContent[], tagName: string): boolean {
  for (const node of nodes) {
    if (node.type !== 'element') continue
    if (node.tagName === tagName) return true
    if (containsTag(node.children, tagName)) return true
  }
  return false
}

function isInsideTableWrapper(parent: Parents | undefined): boolean {
  if (!parent || parent.type !== 'element') return false
  return parent.tagName === 'div' && parent.properties !== undefined && WRAPPER_PROPERTY_NAME in parent.properties
}

// Idempotent: returns early if the table is already inside one of our wrappers.
function wrapTableInScrollContainer(
  table: Element,
  index: number | null | undefined,
  parent: Parents | undefined,
  stats: TableBadgeStats,
) {
  if (!parent || index === undefined || index === null) return
  if (isInsideTableWrapper(parent)) return
  const wrapper: Element = {
    type: 'element',
    tagName: 'div',
    properties: {
      [WRAPPER_PROPERTY_NAME]: '',
      tabIndex: 0,
      role: 'region',
      ariaLabel: 'Scrollable table',
      [STATS_PROPERTY_NAME]: JSON.stringify(stats),
    },
    children: [table],
  }
  parent.children[index] = wrapper
}

// Each table is wrapped in a horizontal scroll container with per-table
// coverage stats attached for downstream telemetry. Badging is decided per
// column: a column is badged only if every non-empty body cell satisfies
// findVariantForCell. Columns with links are skipped so we don't strip <a>.
const transformTables: RehypeHtmlTransformer = tree =>
  visit(tree, 'element', (node, index, parent) => {
    if (node.tagName !== 'table') return

    const stats = newStats()
    const rows = tableBodyRows(node)
    if (rows.length > 0) {
      const cellsByRow = rows.map(rowBodyCells)
      const numCols = Math.max(0, ...cellsByRow.map(rowCells => rowCells.length))
      stats.totalColumns = numCols
      for (let col = 0; col < numCols; col++) {
        const cells = cellsByRow.map(rowCells => rowCells[col]).filter((cell): cell is Element => cell !== undefined)
        const values = cells.map(cell => flattenText(cell.children).trim())

        const hasRealValue = values.some(v => !isEmptyCell(v))
        if (!hasRealValue) {
          stats.rejectionReasons.noRealValues++
          continue
        }
        if (cells.some(cell => containsTag(cell.children, 'a'))) {
          stats.rejectionReasons.hasLink++
          continue
        }

        const variants = values.map(v => (isEmptyCell(v) ? null : findVariantForCell(v)))
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const allEligible = variants.every((variant, i) => isEmptyCell(values[i]!) || variant !== null)
        if (!allEligible) {
          // Distinguish "one stray prose cell cost the column" from "not a
          // categorical column at all".
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const eligibleCount = variants.filter((variant, i) => !isEmptyCell(values[i]!) && variant !== null).length
          if (eligibleCount === 0) stats.rejectionReasons.noEligibleValues++
          else stats.rejectionReasons.mixedValues++
          continue
        }

        stats.badgedColumns++
        for (let i = 0; i < cells.length; i++) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const value = values[i]!
          const variant = variants[i]
          if (!variant) continue
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          cells[i]!.children = [buildBadgeElement(value, variant)]
        }
      }
    }

    wrapTableInScrollContainer(node, index, parent, stats)
  })

const reactComponents: ReactComponentsExtension = {
  span: (props, fallthrough) => {
    const dataProps = props
    const value = dataProps[BADGE_DATA_ATTR]
    if (typeof value !== 'string') return fallthrough
    const rawVariant = dataProps[BADGE_VARIANT_DATA_ATTR]
    const variant = typeof rawVariant === 'string' ? (rawVariant as LabelColorOptions) : undefined
    return <TableBadge value={value} variant={variant} />
  },
  div: (props, fallthrough) => {
    const dataProps = props
    const statsRaw = dataProps[STATS_DATA_ATTR]
    if (typeof statsRaw !== 'string') return fallthrough
    // Strip react-markdown's `node` extra prop before spreading onto the div.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {node, children, ...rest} = props
    return (
      <TableScrollWrapper {...rest} statsRaw={statsRaw}>
        {children}
      </TableScrollWrapper>
    )
  },
}

// `useCombinedComponents` requires a stable extension identity across renders.
const EXTENSION: CopilotMarkdownExtension = {
  transformHtml: transformTables,
  reactComponents,
}

export default function tableBadgesExtension(): CopilotMarkdownExtension {
  return EXTENSION
}
